import { Component, createEffect, createSignal, For, onCleanup, Show } from 'solid-js';
import {
  ComponentDefinition,
  ChangedDocumentService,
  FormComponent,
  FormParameters,
  Template,
  Document
} from '@formbird/types';
import { Subscription } from 'rxjs';

interface DropDownComponentDefinition extends ComponentDefinition {
  multiSelect?: boolean;
  dropDownList?: string[];
  configObjectName?: string;
}

interface DropDownProps {
  fieldValue: any;
  fieldName: string;
  componentDefinition: DropDownComponentDefinition;
  document: Document;
  template: Template;
  formParameters: FormParameters;
  key: string;
}

export function DropDown(props: DropDownProps) {
  const subs = new Subscription();

  const [value, setValue] = createSignal(props.fieldValue);
  const [componentDef, setComponentDef] = createSignal(props.componentDefinition);
  const [options, setOptions] = createSignal<string[]>([]);

  const changedDocumentService = (globalThis as any).FormbirdServiceInjector.get('ChangedDocumentService');
  const dropDownConfigService = (globalThis as any).FormbirdServiceInjector.get('DropDownConfigService');

  createEffect(async () => {
    const def = componentDef();
    if (def?.dropDownList) {
      setOptions(def.dropDownList);
    }

    if (def?.configObjectName && dropDownConfigService) {
      try {
        const result = await dropDownConfigService.searchConfigList(
          def.configObjectName,
          props.fieldName,
          props.template.documentId
        );
        if (result && result.length) {
          setOptions(result);
        }
      } catch (err) {
        console.error('Error fetching dropdown options:', err);
      }
    }
  });

  const docSub = changedDocumentService
    .watch(props.fieldName, props.document.documentId, null)
    .subscribe((newValue: any) => {
      setValue(newValue);
    });
  subs.add(docSub);

  const compSub = changedDocumentService
    .watchComponentDef(props.fieldName, props.template.documentId)
    .subscribe((compDef: any) => {
      setComponentDef({ ...componentDef(), ...compDef });
    });
  subs.add(compSub);

  onCleanup(() => {
    subs.unsubscribe();
  });

  const isDisabled = () => componentDef()?.enabled === false;
  const isMultiple = () => componentDef()?.multiSelect === true;
  const widthClass = () => componentDef()?.fullWidth ? 'w-full' : 'w-72';

  const handleChange = (e: Event) => {
    const target = e.target as HTMLSelectElement;
    let newValue: any;

    if (isMultiple()) {
      newValue = Array.from(target.selectedOptions).map(opt => opt.value);
    } else {
      newValue = target.value;
      // Important: Update the attribute for CSS selector to work
      target.setAttribute('value', newValue);
    }

    setValue(newValue);

    const changeInfo: FormComponent = {
      document: props.document,
      template: props.template,
      componentDefinition: componentDef(),
      fieldValue: newValue,
      fieldName: props.fieldName,
      formParameters: props.formParameters,
      key: props.key,
    };

    changedDocumentService.valueChanged(changeInfo, newValue);
  };

  return (
    <div class={`sb-drop-down-container ${widthClass()}`}>
      <select
        id={`solid-drop-down-${props.document.documentId}-${props.fieldName}`}
        class="sb-drop-down-select"
        onChange={handleChange}
        disabled={isDisabled()}
        multiple={isMultiple()}
        value={value() || ''}
        // @ts-ignore
        attr:value={value() || ''}
      >
        <option value=""></option>
        <For each={options()}>
          {(option) => (
            <option value={option} selected={
              isMultiple()
                ? Array.isArray(value()) && value().includes(option)
                : value() === option
            }>
              {option}
            </option>
          )}
        </For>
      </select>
      <span class="sb-drop-down-highlight"></span>
      <label
        class="sb-drop-down-label"
      >
        {componentDef()?.label}
        {componentDef()?.mandatory && ' *'}
      </label>
    </div>
  );
}
